import type { FC } from 'react'
import React, { useEffect } from 'react'

import Divider from '@vfuk/core-divider'
import { useBreakpoints } from '@vfuk/core-match-media'
import { useLocalTheme } from '@vfuk/core-themes'
import { getDataSelector } from '@vfuk/core-base-props'

import * as Styled from './styles/Footer.style'

import type { FooterProps } from './Footer.types'

import Logo from './components/Logo'
import Social from './components/Social'
import MobileLinkList from './components/MobileLinkList'
import DesktopLinkList from './components/DesktopLinkList'
import PartnerInfo from './components/PartnerInfo'
import LegalLinks from './components/LegalLinks'
import CompanyInfo from './components/CompanyInfo'
import PartnerLinks from './components/PartnerLinks'

import defaultTheme from './themes/Footer.theme'

const Footer: FC<FooterProps> = ({
  appearance = 'primary',
  social,
  partnerLogo,
  legalLinks,
  companyInfo,
  additionalInfo,
  copyrightText,
  partnerLinks,
  linkGroups,
  onInteractionClick,
  onLoad,
  localTheme,
  dataSelectorPrefix,
  id,
  dataAttributes,
}) => {
  const componentName = 'Footer'
  const footerTheme = useLocalTheme(componentName, defaultTheme, localTheme)

  const { inverseLogo, dividerAppearance, inverseDivider, inverseText, inverseIcon } = footerTheme.appearance[appearance]

  const { mdAndBelow } = useBreakpoints()

  useEffect(() => {
    if (onLoad) {
      onLoad()
    }
  }, [])

  return (
    <Styled.Footer
      id={id}
      data-component-name={componentName}
      data-selector={getDataSelector(dataSelectorPrefix)}
      footerTheme={footerTheme}
      appearance={appearance}
      {...dataAttributes}
    >
      <Styled.FooterContainer>
        <Styled.IconsWrapper>
          <Logo inverse={inverseLogo} dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'logo')} />
          {social && (
            <Social
              social={social!}
              onInteractionClick={onInteractionClick}
              inverse={inverseIcon}
              dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'social')}
            />
          )}
        </Styled.IconsWrapper>
        <Styled.DividerWrapper>
          <Divider appearance={dividerAppearance} inverse={inverseDivider} />
        </Styled.DividerWrapper>
        {linkGroups && (
          <>
            {mdAndBelow ? (
              <MobileLinkList
                linkGroups={linkGroups!}
                onInteractionClick={onInteractionClick}
                footerTheme={footerTheme}
                appearance={appearance}
                dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'mobile-link-list')}
              />
            ) : (
              <DesktopLinkList
                linkGroups={linkGroups!}
                onInteractionClick={onInteractionClick}
                inverse={inverseText}
                dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'desktop-link-list')}
              />
            )}
          </>
        )}
        {partnerLogo && (
          <PartnerInfo
            partnerLogo={partnerLogo}
            footerTheme={footerTheme}
            appearance={appearance}
            dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'partner-info')}
          />
        )}
        <LegalLinks
          legalLinks={legalLinks}
          onInteractionClick={onInteractionClick}
          footerTheme={footerTheme}
          appearance={appearance}
          dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'legal-links')}
        />
        <CompanyInfo
          companyInfo={companyInfo}
          additionalInfo={additionalInfo}
          copyrightText={copyrightText}
          inverse={inverseText}
          dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'company-info')}
        />
        {partnerLinks && (
          <PartnerLinks
            partnerLinks={partnerLinks}
            onInteractionClick={onInteractionClick}
            footerTheme={footerTheme}
            appearance={appearance}
            dataSelectorPrefix={getDataSelector(dataSelectorPrefix, 'partner-links')}
          />
        )}
      </Styled.FooterContainer>
    </Styled.Footer>
  )
}

export default Footer
